import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { cn } from "@-/com.npmjs.cn";
import { useChineseSpeechCapture } from "@-/dev.improve.haniverse.lib.voice.chinese";
import { useRevealMutation } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.reveal";
import type { DrillPageSentenceDrill } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.sentence-drill";
import { useTranscriptProducedTokens } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.sentence-drill";
import { View } from "@-/com.npmjs.react-native";
import { NavContext } from "@-/dev.improve.haniverse.routes.nav._context";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useUpdateUserSettings } from "@-/dev.improve.haniverse.lib.user.settings.update";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useRouteBodyState } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";

/**
 * The spoken-mode footer: hold a mic session open and feed what is heard into the drill.
 *
 * The transcript is segmented and given readings before it reaches the state machine, so the
 * matcher can accept a homophone the recognizer spelled wrong — which is the common failure mode
 * for correctly-spoken Mandarin.
 *
 * `markDispatched` is what stops an interim result from resubmitting words already matched: one
 * recognition session grows a single utterance and re-emits all of it on every tick.
 */
export function DrillFooterSentenceSpeechView({
  sentenceDrill,
}: {
  sentenceDrill: DrillPageSentenceDrill;
}) {
  const { dispatch, state } = sentenceDrill;
  const revealMutation = useRevealMutation();
  const resolveProducedTokens = useTranscriptProducedTokens();
  const updateUserSettingsMutation = useUpdateUserSettings();

  // The escape hatch: the FIRST time a speaking card appears in a session, offer a way out for a
  // user who cannot talk right now (a library, a bus, a shared office). Without it their queue is
  // simply stuck — a spoken card has no other way to be answered.
  //
  // Shown once per session rather than on every card: after the first offer the user has seen the
  // option and either took it or chose to keep speaking, so repeating it on each card would just be
  // nagging. The flag resets on relaunch because the circumstance does.
  const [hasSeenSkipOffer, setHasSeenSkipOffer] = useContextSelectors(
    NavContext,
    (c) => c.hasSeenSpokenDrillSkipOfferThisSession,
    (c) => c.setHasSeenSpokenDrillSkipOfferThisSession,
  );
  // Captured at MOUNT via a lazy initializer, not derived each render: the effect below immediately
  // marks the offer as seen, so a derived value would flip to false mid-view and the button would
  // vanish under the user's finger. Snapshotting keeps it visible for the whole of THIS card.
  const [isSkipOfferVisible] = useRouteBodyState(
    "sentence-speech-skip-offer",
    () => !hasSeenSkipOffer,
  );
  const [retainedHeardText, setRetainedHeardText] = useRouteBodyState("sentence-speech-heard", "");
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    // By the time this drill has rendered, the offer HAS been shown — whether or not it is taken.
    setHasSeenSkipOffer(true);
  }, [setHasSeenSkipOffer]);

  const handleCantSpeak = useCallback(() => {
    // Turns off the mic MASTER switch, not the speaking-drill preference: the user is telling us
    // they cannot speak now, not that they no longer want to practise speaking. Review eligibility
    // ANDs the two, so this pulls every mic card from the queue while keeping the preference so it
    // can be turned back on from Settings.
    updateUserSettingsMutation.mutate({ isMicDrillsEnabled: false });
    // End this card too — it is a mic card, so it is exactly what the user just said they cannot do.
    revealMutation.mutate();
  }, [revealMutation, updateUserSettingsMutation]);

  // Bias the recognizer toward the sentence's own vocabulary — both whole words and their
  // characters, since a learner often produces one character of a word at a time.
  const contextualStrings = useMemo(() => {
    const strings = new Set<string>();
    for (const token of state.tokens) {
      if (token.word.length > 0) {
        strings.add(token.word);
      }
    }
    return [...strings];
  }, [state.tokens]);

  const handleTranscript = useCallback(
    (fragment: string) => {
      void (async () => {
        const produced = await resolveProducedTokens(fragment);
        if (mountedRef.current && produced.length > 0) {
          dispatch({ kind: "attempt", produced });
        }
      })();
    },
    [dispatch, resolveProducedTokens],
  );

  const { heardText, isListening, toggle } = useChineseSpeechCapture({
    contextualStrings,
    initialHeardText: retainedHeardText,
    onTranscript: handleTranscript,
  });
  useEffect(() => {
    setRetainedHeardText(heardText);
  }, [heardText, setRetainedHeardText]);

  return (
    <View className="w-full gap-2 px-4 pb-2">
      {/* `accessibilityLabel` mirrors the text because iOS exposes a composed Text's content as the
          element's accessibility label, NOT as `text` — an E2E assertion on the transcript can only
          see it this way (verified from a failing run's screen hierarchy). It doubles as the screen
          reader's rendering of what was heard, which it should announce anyway. */}
      <Text
        accessibilityLabel={heardText.length === 0 ? "Nothing heard yet" : `Heard: ${heardText}`}
        className="min-h-5 text-center text-sm leading-tight"
        colorVariant="muted"
        testID="sentence-drill-heard"
      >
        {heardText}
      </Text>
      {isSkipOfferVisible ? (
        <View className="flex-row">
          <Button
            accessibilityLabel="Can't speak right now? Skip speaking drills"
            onPress={handleCantSpeak}
            size="sm"
            testID="sentence-drill-cant-speak"
            variant="ghost"
          >
            <Text className="text-xs" colorVariant="muted">
              Can&apos;t speak? Skip
            </Text>
          </Button>
        </View>
      ) : null}
      <View className="flex-row items-center justify-between gap-2">
        <Button
          accessibilityLabel="Show a hint for this word"
          onPress={() => {
            dispatch({ kind: "request-hint" });
          }}
          size="sm"
          testID="sentence-drill-hint"
          variant="ghost"
        >
          <Text>Hint</Text>
        </Button>
        <Button
          accessibilityLabel={isListening ? "Stop listening" : "Start speaking"}
          className={cn("flex-1", isListening && "border-2 border-primary")}
          onPress={() => {
            toggle();
          }}
          testID="sentence-drill-mic"
          variant="outline"
        >
          <Text>{isListening ? "Listening…" : "Speak"}</Text>
        </Button>
        <Button
          accessibilityLabel="Reveal this word and finish"
          onPress={() => {
            revealMutation.mutate();
          }}
          size="sm"
          testID="sentence-drill-reveal"
          variant="outline"
        >
          <Text>Reveal</Text>
        </Button>
      </View>
    </View>
  );
}
